import { zodResolver } from "@hookform/resolvers/zod";
import { Person } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { useDispatch } from "react-redux";
import {
  Address,
  Document,
  Relationship,
} from "../../../../@types/subscriptionInfo";
import { useAppSelector } from "../../../../hooks/hooks";
import AddressService from "../../../../services/AdressService";
import {
  IAssociateState,
  addRelationship,
} from "../../../../store/associateSlice";
import { AssociateInput, associateSchema } from "./validations/associateSchema";

interface AssociateFormProps {
  handleNextStep(step: string): void;
  handleBackStep(step: string): void;
  close(): void;
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="00000-000"
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref as any}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

const RgMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function RgMaskCustom(props, ref) {
    const { onChange, ...other } = props;

    const [mask, setMask] = React.useState("00.000.000-0");

    const defineMask = (value: string) => {
      if (value.length === 8) {
        return "000.000.000";
      }
      return "00.000.000-0";
    };

    return (
      <IMaskInput
        {...other}
        mask={mask}
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref as any}
        onAccept={(value: any) => {
          const inputValue = value.replace(/[^0-9]/g, "");
          const mask = defineMask(inputValue);
          setMask(mask);

          onChange({ target: { name: props.name, value } });
        }}
        overwrite
      />
    );
  }
);

const CpfMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function CpfMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="000.000.000-00"
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref as any}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

const MobileMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function MobileMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="(00)00000-0000"
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref as any}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

const AssociateForm: React.FC<AssociateFormProps> = ({
  handleNextStep,
  handleBackStep,
  close,
}) => {
  const dispatch = useDispatch();

  const {
    register,
    reset,
    formState: { errors },
    setError,
    handleSubmit,
    control,
  } = useForm<AssociateInput>({
    resolver: zodResolver(associateSchema),
  });

  const [address, setAddress] = useState<Address | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmitHandler: SubmitHandler<AssociateInput> = (values) => {
    const addressToSubmit: Address = {
      zipCode: values.zipCode,
      neighborhood: values.neighborhood,
      city: values.city,
      state: values.state,
      address: values.address,
      number: values.number,
      additionalInfo: values.additionalInfo,
      ibgeCode: address?.ibgeCode,
    };
    const document: Document = {
      type: "RG",
      number: values.rg,
    };
    const documents: Document[] = [document];
    const relationship: Relationship = {
      name: values.name,
      documentNumber: values.cpf,
      email: values.email,
      mobilePhone: values.mobilePhone,
      address: addressToSubmit,
      relationshipType: values.relationshipType,
      documents: documents,
    };
    dispatch(addRelationship(relationship));
    handleNextStep("associatesList");
  };

  const [values, setValues] = useState({
    zipCode: "",
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      [event.target.name]: event.target.value,
    });
  };

  useEffect(() => {
    const findAddress = async () => {
      const zipCode = values.zipCode.replace(/\D/g, "");
      const isFieldFilled = zipCode.length === 8;
      if (isFieldFilled) {
        try {
          setLoading(true);
          const address = await AddressService.find(zipCode);
          setLoading(false);
          if (address) {
            setAddress(address);
            return;
          }

          setError("zipCode", {
            type: "custom",
            message: "CEP não encontrado",
          });
        } catch (error) {
          setError("zipCode", {
            type: "custom",
            message: "CEP não encontrado",
          });
        }
      }
    };
    findAddress();
  }, [values, setError]);

  useEffect(() => {
    if (address !== null) {
      reset(address);
    }
  }, [address, reset]);

  const relationshipTypes = [
    { value: "SOCIO", text: "Sócio" },
    { value: "REPRESENTANTE_LEGAL", text: "Representante Legal" },
  ];

  const subscriptionInfo = useAppSelector<IAssociateState>(
    (selector) => selector.associateSlice
  );

  const userData: Relationship | undefined =
    subscriptionInfo?.relationships[
      subscriptionInfo?.editingElementPosition ?? 0
    ];

  return (
    <>
      <Box
        gap={2}
        display={"flex"}
        sx={{
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          padding: 2,
          background:
            "linear-gradient(229deg, #41B0F8 0%, #626DFD 50.52%, #7232FB 100%)",
        }}
      >
        <div>
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            sx={{
              bgcolor: "#9880FD",
              padding: 0.5,
              borderRadius: "20px",
            }}
          >
            <Person
              sx={{
                color: "white",
              }}
            />
          </Box>
        </div>

        <Box>
          <Box display={"flex"} gap={0.5}>
            <Typography fontWeight="600" fontSize="14px" color={"white"}>
              03
            </Typography>
            <Typography fontWeight="400" fontSize="14px" color={"white"}>
              de 04
            </Typography>
          </Box>
          <Typography fontWeight="600" fontSize="18px" color={"white"}>
            Dados do Sócio
          </Typography>
        </Box>
        <Box display="flex" style={{ flex: 1, justifyContent: "flex-end" }}>
          <IconButton>
            <CloseIcon sx={{ color: "#fff" }} onClick={close} />
          </IconButton>
        </Box>
      </Box>
      <Box padding={3} display={"flex"} flexDirection={"column"} gap={2}>
        <Typography fontWeight="400" fontSize="16px" color={"black"}>
          Precisamos dos dados do sócio.
        </Typography>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(onSubmitHandler)}
        >
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                defaultValue={userData && userData.name}
                {...register("name")}
                label="Nome Completo"
                error={!!errors["name"]}
                helperText={errors["name"] ? errors["name"].message : ""}
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                defaultValue={userData && userData.email}
                label="Email"
                {...register("email")}
                error={!!errors["email"]}
                helperText={errors["email"] ? errors["email"].message : ""}
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                defaultValue={
                  userData &&
                  userData.mobilePhone &&
                  userData.mobilePhone.startsWith("55") &&
                  userData.mobilePhone.slice(2)
                }
                {...register("mobilePhone")}
                label="Celular"
                error={!!errors["mobilePhone"]}
                helperText={
                  errors["mobilePhone"] ? errors["mobilePhone"].message : ""
                }
                variant="standard"
                InputProps={{
                  inputComponent: MobileMaskCustom as any,
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                label="RG"
                defaultValue={userData?.documents[0]?.number}
                {...register("rg")}
                error={!!errors["rg"]}
                helperText={errors["rg"] ? errors["rg"].message : ""}
                variant="standard"
                InputProps={{
                  inputComponent: RgMaskCustom as any,
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                label="CPF"
                defaultValue={userData && userData.documentNumber}
                {...register("cpf")}
                error={!!errors["cpf"]}
                helperText={errors["cpf"] ? errors["cpf"].message : ""}
                variant="standard"
                InputProps={{
                  inputComponent: CpfMaskCustom as any,
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="relationshipType"
                control={control}
                defaultValue={userData && userData.relationshipType}
                render={({ field }) => (
                  <TextField
                    required
                    fullWidth
                    {...field}
                    select
                    label="Tipo de Sociedade"
                    {...register("relationshipType")}
                    error={!!errors["relationshipType"]}
                    helperText={
                      errors["relationshipType"]
                        ? errors["relationshipType"].message
                        : ""
                    }
                    variant="standard"
                    style={{ display: "flex" }}
                  >
                    {relationshipTypes.map((type) => {
                      return (
                        <MenuItem key={type.value} value={type.value}>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            mr={1}
                            gap={1}
                          >
                            {type.text}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                required
                fullWidth
                label="CEP"
                defaultValue={userData && userData.address?.zipCode}
                {...register("zipCode")}
                onChange={handleChange}
                error={!!errors["zipCode"]}
                helperText={errors["zipCode"] ? errors["zipCode"].message : ""}
                variant="standard"
                InputProps={{
                  inputComponent: TextMaskCustom as any,
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                  endAdornment: (
                    <Box sx={{ width: "20px" }}>
                      {loading && <CircularProgress size="1rem" />}
                    </Box>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                defaultValue={userData && userData.address?.address}
                placeholder="Logradouro"
                {...register("address")}
                error={!!errors["address"]}
                helperText={errors["address"] ? errors["address"].message : ""}
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                required
                fullWidth
                defaultValue={userData && userData.address?.neighborhood}
                placeholder="Bairro"
                {...register("neighborhood")}
                error={!!errors["neighborhood"]}
                helperText={
                  errors["neighborhood"] ? errors["neighborhood"].message : ""
                }
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                placeholder="Estado"
                defaultValue={userData && userData.address?.state}
                {...register("state")}
                error={!!errors["state"]}
                helperText={errors["state"] ? errors["state"].message : ""}
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                fullWidth
                defaultValue={userData && userData.address?.city}
                placeholder="Cidade"
                {...register("city")}
                error={!!errors["city"]}
                helperText={errors["city"] ? errors["city"].message : ""}
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                fullWidth
                defaultValue={userData && userData.address?.number}
                placeholder="Número"
                {...register("number")}
                error={!!errors["number"]}
                helperText={errors["number"] ? errors["number"].message : ""}
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                required
                fullWidth
                defaultValue={userData && userData.address?.additionalInfo}
                placeholder="Complemento"
                {...register("additionalInfo")}
                error={!!errors["additionalInfo"]}
                helperText={
                  errors["additionalInfo"]
                    ? errors["additionalInfo"].message
                    : ""
                }
                variant="standard"
                InputProps={{
                  style: {
                    fontSize: 16,
                    height: 30,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              margin: 2,
            }}
          >
            <Button
              onClick={() => {
                handleBackStep("associatesList");
              }}
              variant="outlined"
              color="secondary"
            >
              Voltar
            </Button>

            <Button type="submit" variant="contained" sx={{ marginLeft: 1 }}>
              Adicionar
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AssociateForm;
